import { cn, Flex } from '@kognitos/lattice';

/**
 * Padding a message list needs so its first and last lines don't come to rest
 * under a fade. Paired with the strip's own height, which they mirror.
 */
export const CHAT_EDGE_FADE_CLEARANCE = {
  top: 'pt-8',
  bottom: 'pb-8',
} as const;

export interface IChatEdgeFadeProps {
  /** Which edge to dissolve into: the header above, or the composer below. */
  edge: 'top' | 'bottom';
  className?: string;
}

/**
 * A 32px gradient that dissolves a scrolling message list into the chat
 * column's edges, so text softens as it leaves rather than cutting off.
 *
 * Sits in the flow with a negative margin, overlapping the list instead of
 * taking a row of its own — which keeps it out of the scroll area, where it
 * would scroll away with the content. The list's matching clearance keeps the
 * fade over empty space at rest, so it only bites while scrolling.
 *
 * 32px rather than the 20px this started at: over a 22px line the shorter
 * strip only caught a line's ascenders, which read as no fade at all.
 *
 * @param props - Component props
 * @param props.edge - Edge to fade into. See {@link IChatEdgeFadeProps}.
 * @param props.className - Extra classes for the strip
 * @returns The fade strip
 */
export function ChatEdgeFade({ edge, className }: IChatEdgeFadeProps) {
  return (
    <Flex
      aria-hidden
      /* Explicit: lattice Flex defaults to `width: auto` and its parents leave
       * column children at `items-start`, which sizes this empty strip to its
       * content — nothing, so no gradient is drawn at all. */
      width="full"
      className={cn(
        'pointer-events-none relative z-10 h-8 shrink-0 from-layout to-transparent',
        edge === 'top' ? '-mb-8 bg-gradient-to-b' : '-mt-8 bg-gradient-to-t',
        className,
      )}
      data-testid={`chat-edge-fade-${edge}`}
    />
  );
}
